import { KatDropdown, KatInput, KatButton } from '@amzn/katal-react';
import { FC, FormEvent, useRef, useState, useEffect } from 'react';
import { ProviderLogo } from '@amzn/fire-tv-campaign-automation-service';

import { AssetState, LogoTextState, InputType } from 'src/types';
import { useSelection } from 'src/contexts/SelectionContext';
import { validateLogoTextState, LogoTextValidationErrors } from 'src/utils/validations/Logo';
import { getRequiredLabel, trimLogoTextState } from 'src/utils/assetInput/assetInputUtils';
import { ErrorConstants, TEXT_BASED_TITLE_DESCRIPTION_LENGTH } from 'src/constants';
import { getLogoTextConfig, FieldConfig } from 'src/configs/logoTextInputConfig';
import { useAssetContext } from 'src/hooks/useAssetContext';
import { AssetType } from '@amzn/fire-tv-campaign-automation-service';

import styles from './LogoTextInput.module.css';

/**
 * Interface representing the props for the LogoTextInput component
 */
interface LogoTextInputProps {
  /** Current state of the asset */
  logoTextState: LogoTextState;
  /** Callback function to update the asset state */
  updateAssetState: (assetState: Partial<AssetState>) => void;
  /** Whether the component should be disabled */
  disabled?: boolean;
}

/**
 * Helper function to check if description is non-empty
 */
const hasNonEmptyDescription = (description: string | undefined): boolean => {
  return Boolean(description && description.trim() !== '');
};

/**
 * Helper function to get max title length based on description presence
 */
const getMaxTitleLength = (description: string | undefined): number => {
  return hasNonEmptyDescription(description)
    ? TEXT_BASED_TITLE_DESCRIPTION_LENGTH.LOGO_TITLE_MAX_LENGTH_WITH_DESCRIPTION
    : TEXT_BASED_TITLE_DESCRIPTION_LENGTH.LOGO_TITLE_MAX_LENGTH_WITHOUT_DESCRIPTION;
};

/**
 * Component for inputting logo text and related information
 * @param props - The component props
 * @param props.assetState - Current state of the asset
 * @param props.updateAssetState - Callback function to update the asset state
 */

export const LogoTextInput: FC<LogoTextInputProps> = ({
  logoTextState,
  updateAssetState,
  disabled = false,
}) => {
  const { locale } = useSelection();
  const { assetStates } = useAssetContext();
  const variant = assetStates[AssetType.LOGO]?.transformationVariant;
  const config = getLogoTextConfig(variant);

  const [currentState, setCurrentState] = useState<LogoTextState>(logoTextState);
  const savedStateRef = useRef<LogoTextState>(logoTextState);
  const [fieldErrors, setFieldErrors] = useState<LogoTextValidationErrors>({});

  // Clear CTA when locale changes
  useEffect(() => {
    setCurrentState((prev) => ({ ...prev, cta: undefined }));
  }, [locale]);

  // Sync externally-set logoTitle (e.g. from auto-populate) into local state.
  // Do NOT update savedStateRef so the "Unsaved Changes" indicator appears.
  useEffect(() => {
    if (
      logoTextState.logoTitle !== undefined &&
      logoTextState.logoTitle !== currentState.logoTitle
    ) {
      setCurrentState((prev) => ({ ...prev, logoTitle: logoTextState.logoTitle }));
    }
  }, [logoTextState.logoTitle]);

  // Detect which fields have changed
  const fieldChanged = Object.keys(config).reduce(
    (acc, key) => {
      const fieldKey = key as keyof LogoTextState;
      acc[fieldKey] = currentState[fieldKey] !== savedStateRef.current[fieldKey];
      return acc;
    },
    {} as Record<keyof LogoTextState, boolean>,
  );

  const hasChanges = Object.values(fieldChanged).some((changed) => changed);

  // Only count real validation errors, not "unsaved changes" warnings
  // All errors except UNSAVED_CHANGES are considered blocking errors that prevent saving
  const hasValidationErrors = Object.values(fieldErrors).some(
    (error) => error !== ErrorConstants.MESSAGES.UNSAVED_CHANGES,
  );

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    const trimmedState = trimLogoTextState(currentState);
    const validationErrors = validateLogoTextState(trimmedState, variant);

    setFieldErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    savedStateRef.current = trimmedState;
    setCurrentState(trimmedState);
    updateAssetState({ ...trimmedState, inputType: InputType.LOGO_TEXT });
  };

  const handleCancel = () => {
    setCurrentState(savedStateRef.current);
    setFieldErrors({});
  };

  // Generic field change handler
  const handleFieldChange = <K extends keyof LogoTextState>(field: K, value: LogoTextState[K]) => {
    const newState = { ...currentState, [field]: value };
    setCurrentState(newState);

    // Check if the new value matches the saved value
    const savedValue = savedStateRef.current[field];
    const hasUnsavedChanges = value !== savedValue;

    setFieldErrors((prev) => {
      const newErrors = { ...prev };

      // Clear any submit-time validation error for the field being changed
      if (newErrors[field] && newErrors[field] !== ErrorConstants.MESSAGES.UNSAVED_CHANGES) {
        delete newErrors[field];
      }

      // Validate title length when title or description changes
      if (field === 'logoTitle' || field === 'logoDescription') {
        const trimmedTitle = newState.logoTitle?.trim();
        const titleLength = newState.logoTitle?.length || 0;
        const maxTitleLength = getMaxTitleLength(newState.logoDescription);
        if (!trimmedTitle && config.logoTitle?.required && field === 'logoTitle') {
          newErrors.logoTitle = ErrorConstants.MESSAGES.LOGO_TITLE_EMPTY;
        } else if (titleLength > maxTitleLength) {
          newErrors.logoTitle = hasNonEmptyDescription(newState.logoDescription)
            ? `Title must be ${maxTitleLength} characters or less (description provided)`
            : `Title must be ${maxTitleLength} characters or less`;
        } else if (
          titleLength <= maxTitleLength &&
          newErrors.logoTitle !== ErrorConstants.MESSAGES.UNSAVED_CHANGES &&
          newErrors.logoTitle !== ErrorConstants.MESSAGES.LOGO_TITLE_EMPTY
        ) {
          delete newErrors.logoTitle;
        }
      }

      // Validate CTA length when CTA or provider logo changes
      if (field === 'cta' || field === 'providerLogo') {
        const ctaLength = newState.cta?.length || 0;

        // Only validate CTA length if provider logo is selected
        if (newState.providerLogo && ctaLength > 0) {
          const ctaMaxLength =
            newState.providerLogo === ProviderLogo.NONE
              ? TEXT_BASED_TITLE_DESCRIPTION_LENGTH.CTA_MAX_LENGTH_NO_LOGO
              : TEXT_BASED_TITLE_DESCRIPTION_LENGTH.CTA_MAX_LENGTH_WITH_LOGO;

          if (ctaLength > ctaMaxLength) {
            newErrors.cta =
              newState.providerLogo !== ProviderLogo.NONE
                ? `This CTA is too long when used with a provider logo (max ${ctaMaxLength} characters)`
                : `CTA must be ${ctaMaxLength} characters or less`;
          } else if (newErrors.cta !== ErrorConstants.MESSAGES.UNSAVED_CHANGES) {
            // Clear validation error if within limit (but preserve unsaved changes warning)
            delete newErrors.cta;
          }
        } else if (newErrors.cta !== ErrorConstants.MESSAGES.UNSAVED_CHANGES) {
          // Clear CTA length error if provider logo is not selected
          delete newErrors.cta;
        }
      }

      // Set unsaved changes warning (but don't overwrite validation errors)
      if (hasUnsavedChanges) {
        const currentError = newErrors[field];
        if (!currentError || currentError === ErrorConstants.MESSAGES.UNSAVED_CHANGES) {
          newErrors[field] = ErrorConstants.MESSAGES.UNSAVED_CHANGES;
        }
      } else {
        // Clear unsaved changes warning if value matches saved state
        if (newErrors[field] === ErrorConstants.MESSAGES.UNSAVED_CHANGES) {
          delete newErrors[field];
        }
      }

      return newErrors;
    });
  };

  // Render a field based on its configuration
  const renderField = (fieldKey: keyof LogoTextState, config: FieldConfig) => {
    const value = currentState[fieldKey];
    const error = fieldErrors[fieldKey];

    const commonProps = {
      label: getRequiredLabel(config.label, config.required),
      placeholder: config.placeholder,
      name: String(fieldKey),
      disabled,
      state: error ? ('error' as const) : undefined,
      stateLabel: error,
      required: config.required,
      'data-testid': config.dataTestId,
    };

    switch (config.type) {
      case 'text':
        // Dynamic maxLength for logoTitle based on description presence
        const maxLength =
          fieldKey === 'logoTitle'
            ? getMaxTitleLength(currentState.logoDescription)
            : config.maxLength;

        return (
          <KatInput
            key={fieldKey}
            {...commonProps}
            value={value as string}
            onInput={(e) =>
              handleFieldChange(fieldKey, e.currentTarget.value as LogoTextState[typeof fieldKey])
            }
            maxlength={maxLength}
          />
        );

      case 'dropdown':
        return (
          <KatDropdown
            key={fieldKey}
            {...commonProps}
            value={value as string}
            options={config.options(locale)}
            onChange={(event: KatDropdown.ChangeEvent) =>
              handleFieldChange(fieldKey, event.detail.value as LogoTextState[typeof fieldKey])
            }
          />
        );

      default:
        return null;
    }
  };

  return (
    <form className={styles.container} onSubmit={handleSubmit} data-testid="logo-text-form">
      {Object.entries(config).map(([key, fieldConfig]) =>
        renderField(key as keyof LogoTextState, fieldConfig),
      )}
      <div className={styles.buttonContainer}>
        <KatButton
          type="button"
          label="Cancel"
          variant="tertiary"
          onClick={handleCancel}
          disabled={disabled || !hasChanges}
          data-testid="logo-text-cancel-button"
        />
        <KatButton
          type="submit"
          label="Save"
          disabled={disabled || !hasChanges || hasValidationErrors}
          data-testid="logo-text-save-button"
        />
      </div>
    </form>
  );
};
